import { FormEvent, ReactNode, useEffect, useRef, useState, type RefObject } from 'react';
import type { PublicReviewer, ReviewIdentifyResponse } from '../../../shared/types';
import { ApiError, reviewApi } from '../../lib/api';
import Confetti from '../Confetti';
import Eyebrow from '../Eyebrow';
import { useModalFocusTrap } from '../useModalFocusTrap';
import { reviewInput, reviewPrimaryGlowButton, reviewQuietButton } from './ReviewUi';

function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  const panelRef = useModalFocusTrap<HTMLDivElement>(onClose);

  return (
    <div className="app-scrim fade-in fixed inset-0 z-50 grid place-items-center p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div ref={panelRef} className="scale-in max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-card border border-line bg-card p-8 shadow-[var(--shadow-ambient-lg)]" role="dialog" aria-modal="true" aria-labelledby="review-dialog-title" tabIndex={-1}>
        <Eyebrow accent>Client review</Eyebrow>
        <h2 id="review-dialog-title" className="title-lg mt-3">{title}</h2>
        {children}
      </div>
    </div>
  );
}

export function IdentityDialog({ busy, error, onClose, onIdentify }: { busy: boolean; error: string | null; onClose: () => void; onIdentify: (name: string, email: string | null) => Promise<void> }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onIdentify(name, email.trim() || null);
  }
  return (
    <Modal title="Add your name" onClose={onClose}>
      <p className="mt-3 text-muted">Add your name so Henry knows whose picks these are. Your email is optional.</p>
      <form className="mt-6" onSubmit={submit}>
        <label className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-faint">Your name<input className={reviewInput} value={name} onChange={(event) => setName(event.target.value)} maxLength={80} required /></label>
        <label className="mt-5 block font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-faint">Email, optional<input className={reviewInput} type="email" value={email} onChange={(event) => setEmail(event.target.value)} maxLength={254} /></label>
        <p className="mt-4 text-xs text-faint">We only use these details to attribute your review.</p>
        {error ? <p className="mt-4 rounded-input bg-error-tint p-3 text-sm text-error" role="alert">{error}</p> : null}
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button type="button" className={reviewQuietButton} onClick={onClose}>Not now</button>
          <button type="submit" className={reviewPrimaryGlowButton} disabled={busy || !name.trim()}>{busy ? 'Saving your name' : 'Continue'}</button>
        </div>
      </form>
    </Modal>
  );
}

export type FinishCeremonyPhase = 'form' | 'done';

export const FINISH_NAME_ERROR = 'Please type your full name so we have it on record.';

interface CeremonySubmissionResult {
  phase: 'done';
  submittedName: string;
  sentAtMs: number;
}

interface CeremonySubmissionOptions {
  name: string;
  currentReviewer: PublicReviewer;
  identify: (body: { display_name: string; email: null }) => Promise<ReviewIdentifyResponse>;
  onReviewerChange: (reviewer: PublicReviewer) => void;
  onFinish: () => Promise<void>;
  now?: () => number;
}

export async function submitReviewCeremony({
  name,
  currentReviewer,
  identify,
  onReviewerChange,
  onFinish,
  now = Date.now,
}: CeremonySubmissionOptions): Promise<CeremonySubmissionResult> {
  const submittedName = name.trim();
  if (submittedName.length < 2) throw new Error(FINISH_NAME_ERROR);

  if (submittedName !== currentReviewer.display_name) {
    const response = await identify({ display_name: submittedName, email: null });
    onReviewerChange(response.reviewer);
  }
  await onFinish();
  return {
    phase: 'done',
    submittedName,
    sentAtMs: now(),
  };
}

export function shouldCloseFinishCeremony(
  key: string,
  phase: FinishCeremonyPhase,
  busy: boolean,
): boolean {
  return key === 'Escape' && phase === 'form' && !busy;
}

export function handleFinishCeremonyKeyDown(
  key: string,
  phase: FinishCeremonyPhase,
  busy: boolean,
  onClose: () => void,
): void {
  if (shouldCloseFinishCeremony(key, phase, busy)) onClose();
}

export function formatFinishDateTime(ms: number): string {
  const date = new Date(ms);
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hours = date.getHours();
  const displayHour = hours % 12 || 12;
  const period = hours < 12 ? 'am' : 'pm';
  return `${weekdays[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}, ${displayHour}:${String(date.getMinutes()).padStart(2, '0')} ${period}`;
}

function ceremonyErrorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return 'We could not submit your review. Mind trying again?';
}

export function FinishDialogDone({
  projectTitle,
  submittedName,
  sentAtMs,
  onClose,
  doneButtonRef,
}: {
  projectTitle: string;
  submittedName: string;
  sentAtMs: number;
  onClose: () => void;
  doneButtonRef?: RefObject<HTMLButtonElement>;
}) {
  const firstName = submittedName.trim().split(/\s+/)[0] || 'there';
  return (
    <div className="relative text-center">
      <Confetti />
      <div className="relative">
        <div className="text-[2rem] leading-none" aria-hidden>
          🎉
        </div>
        <Eyebrow accent className="mt-3 block">
          Sent · {formatFinishDateTime(sentAtMs)}
        </Eyebrow>
        <h2 id="finish-dialog-title" className="title-lg mt-2">
          That's a wrap, {firstName}.
        </h2>
        <p className="mx-auto mt-3 max-w-[42ch] text-[14px] leading-[1.55] text-muted">
          Your review of <strong className="font-semibold text-fg">{projectTitle}</strong> is with
          Henry. He'll take it from here — and if anything else jumps out, you can still make
          changes until the project closes.
        </p>
        <div className="mt-6 flex justify-center">
          <button
            ref={doneButtonRef}
            type="button"
            className={reviewPrimaryGlowButton}
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export function FinishDialog({
  projectTitle,
  reviewer,
  onClose,
  onReviewerChange,
  onFinish,
}: {
  projectTitle: string;
  reviewer: PublicReviewer;
  onClose: () => void;
  onReviewerChange: (reviewer: PublicReviewer) => void;
  onFinish: () => Promise<void>;
}) {
  const [phase, setPhase] = useState<FinishCeremonyPhase>('form');
  const [name, setName] = useState(reviewer.display_name);
  const [submittedName, setSubmittedName] = useState(reviewer.display_name);
  const [sentAtMs, setSentAtMs] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const doneButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useModalFocusTrap<HTMLDivElement>(() => {
    if (phase === 'form' && !busy) onClose();
  });

  useEffect(() => {
    if (phase === 'done') doneButtonRef.current?.focus();
  }, [phase]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await submitReviewCeremony({
        name,
        currentReviewer: reviewer,
        identify: reviewApi.identify,
        onReviewerChange,
        onFinish,
      });
      setSubmittedName(result.submittedName);
      setSentAtMs(result.sentAtMs);
      setPhase(result.phase);
    } catch (submitError) {
      setError(ceremonyErrorMessage(submitError));
      setBusy(false);
    }
  }

  return (
    <div
      className="app-scrim fade-in fixed inset-0 z-50 flex items-center justify-center p-4"
      role="presentation"
      onMouseDown={
        phase === 'form' && !busy
          ? (event) => {
            if (event.target === event.currentTarget) onClose();
          }
          : undefined
      }
    >
      <div
        ref={panelRef}
        className="scale-in relative max-h-[90vh] w-full max-w-md overflow-hidden overflow-y-auto rounded-card border border-line bg-card p-8 shadow-[var(--shadow-ambient-lg)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="finish-dialog-title"
        tabIndex={-1}
      >
        {phase === 'form' ? (
          <form onSubmit={submit}>
            <Eyebrow accent>Submit your review</Eyebrow>
            <h2 id="finish-dialog-title" className="title-lg mt-3">
              You're sending your picks to Henry.
            </h2>
            <p className="mt-3 text-[14px] leading-[1.55] text-muted">
              Your picks, notes and comments on{' '}
              <strong className="font-semibold text-fg">{projectTitle}</strong> go to Henry at
              Sheahan Studios. Nothing is final — you can keep making changes until the project
              closes.
            </p>
            <label className="mt-6 block font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-faint">
              Type your name to sign
              <input
                className={reviewInput}
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={80}
                autoComplete="name"
                autoFocus
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'finish-name-error' : undefined}
              />
            </label>
            {error ? (
              <p
                id="finish-name-error"
                className="mt-3 rounded-input bg-error-tint p-3 text-sm text-error"
                role="alert"
              >
                {error}
              </p>
            ) : null}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <button type="submit" className={reviewPrimaryGlowButton} disabled={busy}>
                {busy ? 'Submitting review' : 'Submit review'}
              </button>
              <button type="button" className={reviewQuietButton} onClick={onClose} disabled={busy}>
                Keep reviewing
              </button>
            </div>
          </form>
        ) : (
          <FinishDialogDone
            projectTitle={projectTitle}
            submittedName={submittedName}
            sentAtMs={sentAtMs}
            onClose={onClose}
            doneButtonRef={doneButtonRef}
          />
        )}
      </div>
    </div>
  );
}
